import Cookies from 'js-cookie';
import router from '../router';
import { clearAllCaches, unregisterServiceWorkers } from './cache-clearer';
import store from '../store';

/**
 * Completely clears all authentication data and forces logout
 */
export async function clearAllAuthData(redirectToLogin = true): Promise<void> {
  try {
    // Clear all browser caches first
    await clearAllCaches();

    // Try to unregister any service workers
    await unregisterServiceWorkers();

    // Clear all localStorage
    localStorage.clear();

    // Clear all cookies
    clearAllCookies();

    // Also clear the specific authentication cookies with proper options
    // Make sure to use the same path that was used when setting the cookie
    Cookies.remove('API_TOKEN', { path: '/' });
    // Clear Vuex auth state
    store.commit('setToken', null);
    store.commit('setUser', null);

    // Double-check critical auth items are cleared
    console.log('Verifying token removal:');
    console.log('- Cookie API_TOKEN:', Cookies.get('API_TOKEN') ? 'Still present' : 'Successfully removed');
    console.log('- LocalStorage user_token:', localStorage.getItem('user_token') ? 'Still present' : 'Successfully removed');

    // Perform additional cleanup as needed
    sessionStorage.clear();

    console.log('All authentication data and caches cleared successfully');

    if (redirectToLogin) {
      // Force navigation to login page
      router.push('/login?clean=true');

      // Force page reload to ensure a clean state if needed
      if (sessionStorage.getItem('force_reload_after_logout') !== 'done') {
        sessionStorage.setItem('force_reload_after_logout', 'done');

        // Use a small timeout to ensure router push has a chance to process
        setTimeout(() => {
          window.location.href = '/login?clean=true&t=' + Date.now();
        }, 100);
      }
    }
  } catch (e) {
    console.error('Error in clearAllAuthData:', e);
    // Still try to redirect even if clearing failed
    if (redirectToLogin) {
      window.location.href = '/login?clean=true&error=true&t=' + Date.now();
    }
  }
}

/**
 * Utility to clear all cookies
 */
function clearAllCookies(): void {
  try {
    const cookies = document.cookie.split(";");

    // Log count of cookies being cleared
    console.log(`Clearing ${cookies.length} cookies`);

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      // Skip empty cookie names
      if (!name) continue;

      // Clear the cookie with multiple path options to ensure it's fully removed
      // First try the root path
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

      // Then try the current path
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${window.location.pathname}`;

      // Also use js-cookie for more reliable removal of common auth cookies
      if (name === 'API_TOKEN' || name.includes('auth') || name.includes('token')) {
        Cookies.remove(name);
        Cookies.remove(name, { path: '/' });
      }

      console.log(`Cleared cookie: ${name}`);
    }
  } catch (e) {
    console.error('Error while clearing cookies:', e);
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return store.getters.isSignedIn;
}

/**
 * Handle authentication errors consistently
 */
export function handleAuthError(error: any): void {
  console.warn("Authentication failed, redirecting to login page", error);
  clearAllAuthData(true);
}